import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider, Stack, Grid, MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PhoneIcon from '@mui/icons-material/Phone';
import { DateField } from '@mui/x-date-pickers/DateField';
import { dbDate, parseDbDate } from '@caredna-marlin/utils/dates';
import { useNavigate } from 'react-router-dom';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { PhoneIntakeFormData, CALLER_RELATIONSHIP_OPTIONS } from '~/types/transition';

interface Props {
  open: boolean;
  onClose: () => void;
}

const EMPTY: PhoneIntakeFormData = {
  callerName: '',
  callerRelationship: '',
  patientName: '',
  dob: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  diagnosisLabel: '',
  diagnosisCode: '',
  requestedProgram: '',
  insurance: '',
  insuranceMemberId: '',
  referringProvider: '',
  referringFacility: '',
  referringPhone: '',
  referringFax: '',
};

const REQUIRED: (keyof PhoneIntakeFormData)[] = [
  'callerName',
  'callerRelationship',
  'patientName',
  'dob',
  'diagnosisLabel',
  'requestedProgram',
  'insurance',
  'referringProvider',
  'referringFacility',
];

const SectionLabel: React.FC<{ children: string }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    fontWeight={600}
    sx={{ display: 'block', mb: 1.5, letterSpacing: 0.8 }}
  >
    {children}
  </Typography>
);

const PhoneIntakeDialog: React.FC<Props> = ({ open, onClose }) => {
  const { createPhoneReferral } = useTransitionContext();
  const navigate = useNavigate();
  const [form, setForm] = useState<PhoneIntakeFormData>(EMPTY);
  const [submitted, setSubmitted] = useState(false);

  const set = (field: keyof PhoneIntakeFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const filledRequired = REQUIRED.filter((f) => form[f].trim() !== '').length;
  const allRequiredFilled = filledRequired === REQUIRED.length;

  const handleSubmit = () => {
    if (!allRequiredFilled) return;
    const newId = createPhoneReferral(form);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setForm(EMPTY);
      onClose();
      navigate(`/transition/referral/${newId}`);
    }, 1200);
  };

  const handleClose = () => {
    setForm(EMPTY);
    setSubmitted(false);
    onClose();
  };

  if (submitted) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: 52, color: 'success.main', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600}>Referral Created</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            AI processing has started. The referral will appear in the pipeline momentarily.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ bgcolor: 'success.50', borderRadius: '50%', p: 0.75, display: 'flex' }}>
            <PhoneIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>New Phone Referral</Typography>
            <Typography variant="caption" color="text.secondary">
              Complete while on the call — submit to trigger immediate AI processing
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Stack spacing={3}>
          {/* Required field progress */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.75,
              py: 1.25,
              bgcolor: allRequiredFilled ? '#f1f8f2' : 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: allRequiredFilled ? '#c8e6c9' : 'grey.200',
              transition: 'all 0.2s',
            }}
          >
            {allRequiredFilled
              ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.1rem', flexShrink: 0 }} />
              : <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: '1.1rem', flexShrink: 0 }} />
            }
            <Typography
              variant="body2"
              fontWeight={allRequiredFilled ? 600 : 400}
              color={allRequiredFilled ? 'success.dark' : 'text.secondary'}
            >
              {allRequiredFilled
                ? 'All required fields complete — ready to submit'
                : `${filledRequired} of ${REQUIRED.length} required fields complete`}
            </Typography>
          </Box>

          {/* Call Details */}
          <Box>
            <SectionLabel>Call Details</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Caller's Name *" fullWidth size="small"
                  value={form.callerName} onChange={set('callerName')}
                  placeholder="First Last"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Caller's Relationship *" fullWidth size="small" select
                  value={form.callerRelationship}
                  onChange={set('callerRelationship')}
                >
                  <MenuItem value="" disabled><em>Select relationship</em></MenuItem>
                  {CALLER_RELATIONSHIP_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Patient */}
          <Box>
            <SectionLabel>Patient</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Patient Name *" fullWidth size="small"
                  value={form.patientName} onChange={set('patientName')}
                  placeholder="First Last"
                />
              </Grid>
              <Grid item xs={4}>
                <DateField
                  label="Date of Birth *"
                  fullWidth
                  size="small"
                  clearable
                  maxDate={new Date()}
                  value={form.dob ? parseDbDate(form.dob) : null}
                  onChange={(date) => {
                    const dob = date && !isNaN(date.getTime()) ? dbDate(date) : '';
                    setForm((prev) => ({ ...prev, dob }));
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Phone" fullWidth size="small"
                  value={form.phone} onChange={set('phone')}
                  placeholder="(___) ___-____"
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  label="Street Address" fullWidth size="small"
                  value={form.address} onChange={set('address')}
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="City" fullWidth size="small"
                  value={form.city} onChange={set('city')}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="State" fullWidth size="small"
                  value={form.state} onChange={set('state')}
                  placeholder="CO"
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Zip" fullWidth size="small"
                  value={form.zip} onChange={set('zip')}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Clinical */}
          <Box>
            <SectionLabel>Clinical</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Primary Diagnosis *" fullWidth size="small"
                  value={form.diagnosisLabel} onChange={set('diagnosisLabel')}
                  placeholder="e.g. Congestive Heart Failure"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="ICD-10 Code" fullWidth size="small"
                  value={form.diagnosisCode} onChange={set('diagnosisCode')}
                  placeholder="e.g. I50.9"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Requested Program *" fullWidth size="small"
                  value={form.requestedProgram} onChange={set('requestedProgram')}
                  placeholder="e.g. Home Health, Palliative Care"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Insurance */}
          <Box>
            <SectionLabel>Insurance</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Insurance / Payer *" fullWidth size="small"
                  value={form.insurance} onChange={set('insurance')}
                  placeholder="e.g. Aetna, Medicare, UnitedHealthcare"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Member ID" fullWidth size="small"
                  value={form.insuranceMemberId} onChange={set('insuranceMemberId')}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Referring Provider */}
          <Box>
            <SectionLabel>Referring Provider</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Provider Name *" fullWidth size="small"
                  value={form.referringProvider} onChange={set('referringProvider')}
                  placeholder="Dr. First Last"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Facility *" fullWidth size="small"
                  value={form.referringFacility} onChange={set('referringFacility')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Provider Phone" fullWidth size="small"
                  value={form.referringPhone} onChange={set('referringPhone')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Provider Fax" fullWidth size="small"
                  value={form.referringFax} onChange={set('referringFax')}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          disabled={!allRequiredFilled}
          onClick={handleSubmit}
          sx={{ minWidth: 220 }}
        >
          Create Referral &amp; Begin Processing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneIntakeDialog;
